/** What the two 웹 서비스 pages share: the tab switch, the scope picker and the
 *  words for the layout's values. The values themselves are each service's
 *  own (internal/webusage documents the layout); the names here cover the
 *  ones every service is expected to use and show anything else as it is. */

import type { CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Segmented, useQueryParam } from '@/ui'

/** 사용량's two halves. The window travels with the switch; the platform
 *  filter belongs to the apps and stays behind. */
export function UsageTabs({ value }: { value: 'apps' | 'web' }) {
  const navigate = useNavigate()
  const { search } = useLocation()
  const days = new URLSearchParams(search).get('days')
  const q = days ? `?days=${days}` : ''
  return (
    <Segmented
      label="사용량 종류"
      options={[
        { value: 'apps', label: '앱', title: '앱이 보내는 사용 기록' },
        { value: 'web', label: '웹 서비스', title: '웹 서비스가 Cloudflare에 남기는 이용 기록' },
      ]}
      value={value}
      onChange={(v) => navigate(v === 'web' ? `/analytics/web${q}` : `/analytics${q}`)}
    />
  )
}

/** Whether the operator's own use counts: the admin plan and deploy checks
 *  (utm_source=verify) are left out unless asked for. Kept in the address. */
export function useInternal(): [boolean, (on: boolean) => void] {
  const [raw, set] = useQueryParam('internal')
  return [raw === '1', (on) => set(on ? '1' : '')]
}

export function ScopePicker({ internal, onChange }: { internal: boolean; onChange: (on: boolean) => void }) {
  return (
    <Segmented
      label="범위"
      options={[
        { value: '', label: '방문자', title: '관리자 계정과 배포 확인 방문을 뺀다' },
        { value: '1', label: '운영자 포함', title: '관리자 계정과 배포 확인 방문까지 센다' },
      ]}
      value={internal ? '1' : ''}
      onChange={(v) => onChange(v === '1')}
    />
  )
}

export function surfaceLabel(s: string): string {
  switch (s) {
    case 'app':
      return '앱'
    case 'read':
      return '읽기 페이지'
    case '':
      return '—'
    default:
      return s
  }
}

export function planLabel(p: string): string {
  switch (p) {
    case 'anon':
      return '로그인 안 함'
    case 'free':
      return '무료'
    case 'plus':
      return 'Plus'
    case 'pro':
      return 'Pro'
    case 'admin':
      return '관리자'
    case '':
      return '구분 없음'
    default:
      return p
  }
}

export function sourceLabel(s: string): string {
  return s || '직접·알 수 없음'
}

export function deviceLabel(d: string): string {
  switch (d) {
    case 'phone':
      return '폰'
    case 'tablet':
      return '태블릿'
    case 'desktop':
      return '데스크톱'
    case '':
      return '—'
    default:
      return d
  }
}

/** A share as a whole percent: "37%", or a dash when there is nothing to
 *  divide by. */
export function share(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((100 * part) / whole)}%` : '—'
}

/** A heatmap cell's shading: how big `v` is next to the largest value in the
 *  table. The number is always printed, so the shade carries nothing alone. */
export function heat(v: number, top: number): CSSProperties | undefined {
  if (v <= 0 || top <= 0) return undefined
  return { backgroundColor: `rgb(var(--chart-1) / ${(0.1 + 0.5 * (v / top)).toFixed(2)})` }
}
